import { useCallback } from 'react';
import createPersistedState from 'use-persisted-state';

import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { FilterCategoryId } from '~/routes/sing-a-song/song-selection/components/filters/filter-options';
import { FilterableSong } from '~/routes/sing-a-song/song-selection/components/filters/use-filter-categories';
import {
  AppliedFilters,
  applyFilterCategories,
  isFilterSet,
} from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

/**
 * The filter categories the phone's own song list offers, in order.
 *
 * Five of F2's six. `playlist` is missing on purpose: a playlist is not an attribute of a song but a
 * view the TV builds - the popular chart it fetched, the recommendations it drew from the profiles
 * on its player slots, the songs the phones in the room saved - so the phone cannot answer it from
 * the catalogue it holds, and the mirrored song-selection toolbar is where a phone already picks
 * one. The phone's own axis is the All songs / Your list pair beside this.
 */
export const REMOTE_FILTER_CATEGORIES: FilterCategoryId[] = ['genre', 'language', 'duets', 'played', 'length'];

/** The keys those five categories write, which is what "anything on" and "clear all" mean here. */
const FILTER_KEYS: Array<keyof AppliedFilters> = ['genre', 'language', 'duet', 'played', 'length'];

const useFilterState = createPersistedState<AppliedFilters>('remote-mic-song-filters');

/** Shared instances, so an empty filter set cannot invalidate a memo keyed off it every render. */
const NO_FILTERS: AppliedFilters = {};
const NO_IDS: string[] = [];

// Counted by category rather than by pick: Genre, Language and Length each hold as many as the
// user wants, and "3" on the funnel meaning three genres rather than three categories would say
// the list had been narrowed three ways when it had been narrowed one.
const countActiveFilters = (filters: AppliedFilters) => FILTER_KEYS.filter((key) => isFilterSet(filters[key])).length;

/**
 * The phone's half of F15's "the same six filter categories natively". The categories themselves are
 * `useFilterCategories`, shared with the TV's panel; this holds what the phone has picked and applies
 * it with the game's own `filteringFunctions`, so a genre set here and one set on the TV are the same
 * question rather than two implementations of it.
 */
export default function useRemoteFilters() {
  const [filters, setFilters] = useFilterState(NO_FILTERS);
  // Played is the one category that is not an attribute of the song: only the TV knows what the room
  // has sung, so it arrives on its own channel rather than being worked out here.
  const playedSongIds = useSubscription('played-songs') ?? NO_IDS;

  const filterSongs = useCallback(
    <T extends FilterableSong>(list: T[]): T[] =>
      applyFilterCategories(list, {
        ...filters,
        // Carried only when the category is on, exactly as `useSongListFilter` does it - the ids are
        // useless to every other filter and would invalidate the work for nothing.
        ...(filters.played ? { playedSongIds } : {}),
      }),
    [filters, playedSongIds],
  );

  return { filters, setFilters, filterSongs, activeCount: countActiveFilters(filters) };
}
